<template>
  <v-dialog
    v-model="visible"
    fullscreen
    hide-overlay
    transition="dialog-bottom-transition"
  >
    <v-card class="d-flex flex-column h-100 modal-surface" flat tile>
      <!-- Top Fixed Toolbar -->
      <v-toolbar flat color="white" class="border-bottom flex-grow-0 px-4 px-md-6" height="74">
        <div class="d-flex align-center">
          <v-avatar size="44" color="teal lighten-5" class="mr-3 rounded-xl">
            <v-icon color="teal" small>fas fa-chart-pie</v-icon>
          </v-avatar>
          <div>
            <div class="d-flex align-center flex-wrap" style="gap: 8px">
              <span class="caption font-weight-black text-uppercase teal--text text--darken-3 tracking-wide">
                Ficha 360° · Histórico Comercial
              </span>
              <v-chip v-if="customer && customer.rut" x-small label outlined class="font-weight-bold grey--text text--darken-2">
                {{ customer.rut }}
              </v-chip>
            </div>
            <h2 class="text-h6 font-weight-black grey--text text--darken-4 mb-0 text-truncate tracking-tight" style="max-width: 520px">
              {{ customerTitle }}
            </h2>
          </div>
        </div>

        <v-spacer></v-spacer>

        <!-- Timezone Badge & Quick Actions -->
        <div class="d-flex align-center flex-wrap" style="gap: 8px">
          <v-chip v-if="updatedAt" x-small color="grey lighten-4" class="grey--text text--darken-3 font-weight-medium d-none d-sm-inline-flex">
            <v-icon left x-small color="teal">fas fa-clock</v-icon>
            Actualizado {{ updatedAt | formatDateTime }} ({{ serverTimezone || companyTimeZone }})
          </v-chip>

          <v-btn
            v-if="customerId"
            outlined
            small
            color="grey darken-2"
            class="rounded-lg text-none font-weight-bold"
            @click="goToCustomerEdit"
          >
            <v-icon left x-small>fas fa-user-edit</v-icon>
            Editar Cliente
          </v-btn>

          <v-btn
            icon
            color="grey darken-2"
            class="ml-1"
            aria-label="Cerrar modal"
            @click="close"
          >
            <v-icon>fas fa-times</v-icon>
          </v-btn>
        </div>
      </v-toolbar>

      <!-- Scrollable Main Container -->
      <div class="flex-grow-1 overflow-y-auto px-4 px-md-8 py-6">
        <!-- Time Control & Quick Presets Bar -->
        <v-card flat outlined class="filter-card pa-4 mb-6 shadow-xs">
          <v-row align="center" dense>
            <v-col cols="12" md="7">
              <div class="d-flex align-center flex-wrap" style="gap: 6px">
                <span class="caption font-weight-bold grey--text text--darken-2 mr-2 text-uppercase">
                  Período:
                </span>
                <v-btn
                  v-for="preset in presets"
                  :key="preset.id"
                  x-small
                  depressed
                  :color="activePreset === preset.id ? 'primary' : 'grey lighten-4'"
                  :class="activePreset === preset.id ? 'white--text font-weight-bold' : 'grey--text text--darken-3 font-weight-medium'"
                  class="rounded-lg text-none px-3 py-2"
                  @click="applyPreset(preset)"
                >
                  {{ preset.label }}
                </v-btn>
              </div>
            </v-col>

            <v-col cols="12" md="5" class="d-flex align-center justify-md-end flex-wrap" style="gap: 8px">
              <div class="d-flex align-center" style="gap: 8px">
                <v-text-field
                  v-model="startDate"
                  label="Desde"
                  outlined
                  dense
                  hide-details
                  type="date"
                  class="compact-date-input"
                  @change="onManualDateChange"
                />
                <span class="grey--text">—</span>
                <v-text-field
                  v-model="endDate"
                  label="Hasta"
                  outlined
                  dense
                  hide-details
                  type="date"
                  class="compact-date-input"
                  @change="onManualDateChange"
                />
                <v-btn
                  icon
                  small
                  color="primary"
                  :loading="loading"
                  title="Actualizar datos"
                  @click="load(true)"
                >
                  <v-icon small>fas fa-sync-alt</v-icon>
                </v-btn>
              </div>
            </v-col>
          </v-row>
        </v-card>

        <!-- Error State with Retry Button -->
        <v-alert
          v-if="error"
          dense
          outlined
          type="error"
          class="mb-6 rounded-xl bg-white shadow-xs"
        >
          <div class="d-flex align-center justify-space-between flex-wrap" style="gap: 8px">
            <span>{{ error }}</span>
            <v-btn small color="error" depressed class="rounded-lg text-none font-weight-bold" @click="load(true)">
              <v-icon left small>fas fa-redo</v-icon>
              Reintentar
            </v-btn>
          </div>
        </v-alert>

        <!-- Loading Skeletons -->
        <v-row v-if="loading && !asOf" dense class="mb-6">
          <v-col v-for="i in 4" :key="'sk-' + i" cols="12" sm="6" md="3">
            <v-skeleton-loader type="card" height="136" class="rounded-xl shadow-xs" />
          </v-col>
        </v-row>

        <template v-else>
          <!-- Row 1: Hero KPIs (4 Master Cards) -->
          <v-row dense class="mb-6">
            <!-- KPI 1: Venta Confirmada -->
            <v-col cols="12" sm="6" md="3">
              <v-card flat class="hero-kpi-card h-100 is-teal">
                <v-card-text class="pa-5">
                  <div class="d-flex align-center justify-space-between mb-2">
                    <div class="kpi-label teal--text text--darken-3">Venta Confirmada</div>
                    <v-tooltip bottom max-width="260">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon v-bind="attrs" v-on="on" x-small color="grey">fas fa-info-circle</v-icon>
                      </template>
                      <span>Monto neto de cotizaciones aprobadas con Orden de Trabajo para este cliente.</span>
                    </v-tooltip>
                  </div>
                  <div class="kpi-value grey--text text--darken-4">{{ kpis.confirmed | moneda }}</div>
                  <div class="kpi-footer d-flex align-center justify-space-between mt-3 pt-2">
                    <span class="caption grey--text text--darken-2 font-weight-medium">
                      {{ kpis.confirmed_count || 0 }} OT confirmadas
                    </span>
                    <span v-if="kpis.vs_previous_pct != null" class="delta" :class="deltaClass(kpis.vs_previous_pct)">
                      {{ deltaLabel(kpis.vs_previous_pct) }}
                    </span>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>

            <!-- KPI 2: Total Cotizado -->
            <v-col cols="12" sm="6" md="3">
              <v-card flat class="hero-kpi-card h-100">
                <v-card-text class="pa-5">
                  <div class="d-flex align-center justify-space-between mb-2">
                    <div class="kpi-label">Total Cotizado</div>
                    <v-tooltip bottom max-width="260">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon v-bind="attrs" v-on="on" x-small color="grey">fas fa-info-circle</v-icon>
                      </template>
                      <span>Monto total de todas las cotizaciones creadas en el período.</span>
                    </v-tooltip>
                  </div>
                  <div class="kpi-value grey--text text--darken-4">{{ kpis.quoted | moneda }}</div>
                  <div class="kpi-footer d-flex align-center justify-space-between mt-3 pt-2">
                    <span class="caption grey--text text--darken-2 font-weight-medium">
                      {{ kpis.count || 0 }} cotizaciones
                    </span>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>

            <!-- KPI 3: Facturado Neto -->
            <v-col cols="12" sm="6" md="3">
              <v-card flat class="hero-kpi-card h-100">
                <v-card-text class="pa-5">
                  <div class="d-flex align-center justify-space-between mb-2">
                    <div class="kpi-label">Facturado Neto</div>
                    <v-tooltip bottom max-width="260">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon v-bind="attrs" v-on="on" x-small color="grey">fas fa-info-circle</v-icon>
                      </template>
                      <span>Facturas emitidas descontando Notas de Crédito emitidas en el período.</span>
                    </v-tooltip>
                  </div>
                  <div class="kpi-value grey--text text--darken-4">{{ kpis.invoiced | moneda }}</div>
                  <div class="kpi-footer d-flex align-center justify-space-between mt-3 pt-2">
                    <span class="caption grey--text text--darken-2 font-weight-medium">
                      {{ kpis.invoice_count || 0 }} DTEs emitidos
                    </span>
                    <span v-if="kpis.vs_previous_invoiced_pct != null" class="delta" :class="deltaClass(kpis.vs_previous_invoiced_pct)">
                      {{ deltaLabel(kpis.vs_previous_invoiced_pct) }}
                    </span>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>

            <!-- KPI 4: Conversión y Facturación -->
            <v-col cols="12" sm="6" md="3">
              <v-card flat class="hero-kpi-card h-100">
                <v-card-text class="pa-5">
                  <div class="d-flex align-center justify-space-between mb-2">
                    <div class="kpi-label">Conversión Comercial</div>
                    <v-tooltip bottom max-width="260">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon v-bind="attrs" v-on="on" x-small color="grey">fas fa-info-circle</v-icon>
                      </template>
                      <span>Porcentaje de lo cotizado que se convirtió en órdenes de trabajo confirmadas.</span>
                    </v-tooltip>
                  </div>
                  <div class="kpi-value primary--text">{{ kpis.conversion || 0 }}%</div>
                  <div class="kpi-footer d-flex align-center justify-space-between mt-3 pt-2">
                    <span class="caption grey--text text--darken-2 font-weight-medium">
                      Cobrado: {{ kpis.billed_ratio || 0 }}% de lo vendido
                    </span>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <!-- Row 2: Secondary Pipeline Cards (Órdenes Vigentes y Cotizaciones Abiertas) -->
          <v-row dense class="mb-6">
            <v-col cols="12" sm="6">
              <v-card flat class="pipeline-summary-card h-100 pa-5">
                <div class="d-flex align-center justify-space-between">
                  <div>
                    <div class="caption font-weight-bold text-uppercase amber--text text--darken-3">
                      Órdenes Vigentes Abiertas
                    </div>
                    <div class="text-h5 font-weight-black grey--text text--darken-4 mt-1">
                      {{ kpis.open_orders_amount | moneda }}
                    </div>
                    <div class="caption grey--text text--darken-2 font-weight-medium mt-1">
                      {{ kpis.open_orders_count || 0 }} órdenes de trabajo actualmente en proceso
                    </div>
                  </div>
                  <v-avatar size="48" color="amber lighten-5" class="rounded-xl">
                    <v-icon color="amber darken-2">fas fa-industry</v-icon>
                  </v-avatar>
                </div>
              </v-card>
            </v-col>

            <v-col cols="12" sm="6">
              <v-card flat class="pipeline-summary-card h-100 pa-5">
                <div class="d-flex align-center justify-space-between">
                  <div>
                    <div class="caption font-weight-bold text-uppercase deep-purple--text text--darken-2">
                      Cotizaciones en Espera
                    </div>
                    <div class="text-h5 font-weight-black grey--text text--darken-4 mt-1">
                      {{ kpis.open_quotes_amount | moneda }}
                    </div>
                    <div class="caption grey--text text--darken-2 font-weight-medium mt-1">
                      {{ kpis.open_quotes_count || 0 }} cotizaciones pendientes de aprobación o emisión de OT
                    </div>
                  </div>
                  <v-avatar size="48" color="deep-purple lighten-5" class="rounded-xl">
                    <v-icon color="deep-purple darken-1">fas fa-file-invoice-dollar</v-icon>
                  </v-avatar>
                </div>
              </v-card>
            </v-col>
          </v-row>

          <!-- Row 3: Temporal Evolution Chart -->
          <v-card class="panel-card mb-6 shadow-xs" flat>
            <div class="legend-row">
              <div>
                <div class="panel-title">Evolución Histórica</div>
                <div class="panel-sub">{{ chartSubtitle }}</div>
              </div>
              <div class="legend">
                <span><i class="swatch quoted"></i> Cotizado</span>
                <span><i class="swatch sold"></i> Vendido</span>
                <span><i class="swatch billed"></i> Facturado</span>
              </div>
            </div>
            <div v-if="!hasMovement" class="empty-mini py-12 text-center">
              Sin movimientos registrados en este período
            </div>
            <div v-else style="height: 300px">
              <line-chart :key="chartKey" :chart-data="lineData" :options="lineOptions" :height="300" />
            </div>
          </v-card>

          <!-- Row 4: Operative Table - Órdenes Vigentes -->
          <v-card class="panel-card mb-6 shadow-xs" flat>
            <div class="d-flex align-center justify-space-between mb-1">
              <div class="panel-title">Órdenes de Trabajo Vigentes</div>
              <span class="caption grey--text font-weight-bold">{{ openOrders.length }} órdenes activas</span>
            </div>
            <div class="panel-sub mb-4">Detalle de trabajos en curso y saldos pendientes de facturación</div>

            <div v-if="!openOrders.length" class="empty-mini py-6 text-center">
              No hay órdenes de trabajo abiertas para este cliente
            </div>
            <v-simple-table v-else class="history-table">
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-left">OT</th>
                    <th class="text-left">Trabajo</th>
                    <th class="text-left">Entrega</th>
                    <th class="text-right">Venta Total</th>
                    <th class="text-right">Facturado</th>
                    <th class="text-right">Pendiente Facturar</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="row in openOrders"
                    :key="'ot-' + row.id"
                    class="click-row"
                    @click="openWorkOrder(row.id)"
                  >
                    <td class="font-weight-bold teal--text text--darken-3">#{{ row.id }}</td>
                    <td>
                      <div class="rank-name font-weight-bold">{{ row.name || 'Sin nombre' }}</div>
                      <div v-if="row.products && row.products.length" class="rank-meta">
                        {{ row.products.join(' · ') }}
                      </div>
                    </td>
                    <td class="caption font-weight-medium grey--text text--darken-2">
                      {{ formatDay(row.delivery_date) }}
                    </td>
                    <td class="text-right font-weight-bold">{{ row.total | moneda }}</td>
                    <td class="text-right grey--text text--darken-2">{{ row.invoiced | moneda }}</td>
                    <td class="text-right font-weight-bold" :class="{ 'is-loss': Number(row.pending || 0) > 0 }">
                      {{ row.pending | moneda }}
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card>

          <!-- Row 5: 3 Operational Panels (Cotizaciones, Facturas, Top Productos) -->
          <v-row dense>
            <!-- Panel 1: Cotizaciones Abiertas -->
            <v-col cols="12" md="4">
              <v-card class="panel-card h-100 shadow-xs" flat>
                <div class="panel-title">Cotizaciones Abiertas</div>
                <div class="panel-sub mb-4">Sin orden de trabajo asociada</div>
                <div v-if="!openQuotes.length" class="empty-mini py-6 text-center">Sin cotizaciones abiertas</div>
                <div
                  v-for="row in openQuotes"
                  :key="'oq-' + row.id"
                  class="mini-row"
                  @click="openQuote(row.id)"
                >
                  <div>
                    <div class="rank-name font-weight-bold">{{ row.name || ('Cotización #' + row.id) }}</div>
                    <div class="rank-meta">#{{ row.id }} · {{ quoteStatus(row.status) }} · {{ formatDate(row.created_at) }}</div>
                  </div>
                  <div class="rank-val font-weight-bold">{{ row.total | moneda }}</div>
                </div>
              </v-card>
            </v-col>

            <!-- Panel 2: Últimas Facturas -->
            <v-col cols="12" md="4">
              <v-card class="panel-card h-100 shadow-xs" flat>
                <div class="panel-title">Últimos Documentos Tributarios</div>
                <div class="panel-sub mb-4">Facturas y Notas de Crédito recientes</div>
                <div v-if="!latestInvoices.length" class="empty-mini py-6 text-center">Sin documentos emitidos</div>
                <div
                  v-for="row in latestInvoices"
                  :key="'i-' + row.id"
                  class="mini-row"
                  @click="openInvoice(row.id)"
                >
                  <div>
                    <div class="rank-name font-weight-bold">{{ invoiceLabel(row) }}</div>
                    <div class="rank-meta">
                      {{ formatDay(row.date_emited) }}
                      <span v-if="row.work_order_id"> · OT #{{ row.work_order_id }}</span>
                    </div>
                  </div>
                  <div class="rank-val font-weight-bold" :class="{ 'is-loss': Number(row.total || 0) < 0 }">
                    {{ row.total | moneda }}
                  </div>
                </div>
              </v-card>
            </v-col>

            <!-- Panel 3: Productos Más Vendidos -->
            <v-col cols="12" md="4">
              <v-card class="panel-card h-100 shadow-xs" flat>
                <div class="panel-title">Productos Más Vendidos</div>
                <div class="panel-sub mb-4">Ventas confirmadas del período</div>
                <div v-if="!products.length" class="empty-mini py-6 text-center">Sin productos registrados</div>
                <div
                  v-for="row in products"
                  :key="'p-' + (row.sku || row.name)"
                  class="rank-row"
                >
                  <div class="d-flex align-center justify-space-between">
                    <div class="rank-name font-weight-bold text-truncate" style="max-width: 65%">{{ row.name }}</div>
                    <div class="rank-val font-weight-bold">{{ row.sales | moneda }}</div>
                  </div>
                  <div class="rank-meta mb-1">{{ row.sku || 'Sin SKU' }} · {{ row.qty }} un.</div>
                  <div class="rank-track">
                    <div class="rank-fill is-teal" :style="{ width: rankWidth(row.sales, maxProduct) }"></div>
                  </div>
                </div>
              </v-card>
            </v-col>
          </v-row>
        </template>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import { fetchCustomerCommercialDashboard } from '@/api/reports'
import LineChart from '@/components/charts/LineChart.vue'
import notifications from '@/utils/notifications'
import moment from 'moment'
import numeral from 'numeral'

export default {
  name: 'CustomerHistoryModal',
  components: { LineChart },
  filters: {
    formatDateTime(val) {
      if (!val) return ''
      return moment(val).format('DD/MM/YYYY HH:mm')
    }
  },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    customerId: {
      type: [Number, String],
      default: null
    },
    initialStartDate: {
      type: String,
      default: ''
    },
    initialEndDate: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      loading: false,
      requesting: false,
      error: '',
      activePreset: 'ytd',
      startDate: '',
      endDate: '',
      lastQuery: '',
      asOf: '',
      updatedAt: '',
      serverTimezone: '',
      customer: {},
      presets: [
        { id: 'today', label: 'Hoy' },
        { id: '7d', label: '7 días' },
        { id: 'month', label: 'Mes actual' },
        { id: 'prev_month', label: 'Mes anterior' },
        { id: 'qtd', label: 'Trimestre (QTD)' },
        { id: 'ytd', label: 'Año (YTD)' }
      ],
      lineData: { labels: [], datasets: [] },
      kpis: {
        quoted: 0,
        count: 0,
        confirmed: 0,
        confirmed_count: 0,
        conversion: 0,
        invoiced: 0,
        invoice_count: 0,
        billed_ratio: 0,
        vs_previous_pct: null,
        vs_previous_invoiced_pct: null,
        open_orders_count: 0,
        open_orders_amount: 0,
        open_quotes_count: 0,
        open_quotes_amount: 0
      },
      daily: [],
      openOrders: [],
      openQuotes: [],
      latestInvoices: [],
      products: []
    }
  },
  computed: {
    visible: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      }
    },
    companyTimeZone() {
      return (
        this.$store.getters.getCompany?.current_time_zone ||
        'America/Santiago'
      )
    },
    customerTitle() {
      if (!this.customer || !this.customer.name) return 'Cargando cliente...'
      return this.customer.fantasy || this.customer.name
    },
    hasMovement() {
      return (this.chartRows || []).some(
        (row) => Number(row.quoted || 0) || Number(row.confirmed || 0) || Number(row.invoiced || 0)
      )
    },
    maxProduct() {
      let max = 1
      ;(this.products || []).forEach((row) => {
        const value = Number(row.sales || 0)
        if (value > max) max = value
      })
      return max
    },
    chartGrain() {
      return this.activePreset === 'ytd' || this.activePreset === 'qtd' ? 'month' : 'day'
    },
    chartSubtitle() {
      if (this.chartGrain === 'month') return 'Cotizado, vendido y facturado mes a mes'
      return 'Cotizado, vendido y facturado día a día'
    },
    chartKey() {
      return (
        String(this.customerId) +
        '-' +
        (this.startDate || '') +
        '-' +
        (this.endDate || '') +
        '-' +
        this.chartGrain +
        '-' +
        this.chartRows.length
      )
    },
    chartRows() {
      if (this.chartGrain === 'month') return this.monthlyFromDaily(this.daily)
      return this.daily || []
    },
    lineOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        tooltips: {
          mode: 'index',
          intersect: false,
          backgroundColor: '#ffffff',
          titleFontColor: '#0f172a',
          bodyFontColor: '#475569',
          borderColor: '#e2e8f0',
          borderWidth: 1,
          cornerRadius: 8,
          xPadding: 12,
          yPadding: 12,
          callbacks: {
            label: (item, data) => `${data.datasets[item.datasetIndex].label}: $${numeral(item.yLabel).format('0,0')}`
          }
        },
        scales: {
          xAxes: [
            {
              gridLines: { display: false },
              ticks: { fontColor: '#94a3b8', fontSize: 11, maxRotation: 0, autoSkip: true, maxTicksLimit: 12 }
            }
          ],
          yAxes: [
            {
              gridLines: { color: '#f1f5f9', borderDash: [4, 4], drawBorder: false },
              ticks: {
                fontColor: '#94a3b8',
                fontSize: 11,
                callback: (value) => '$' + numeral(value).format('0,0a')
              }
            }
          ]
        }
      }
    }
  },
  watch: {
    value(isOpen) {
      if (isOpen && this.customerId) {
        this.initializeDates()
        this.load(true)
      }
    },
    customerId(newId) {
      if (newId && this.visible) {
        this.initializeDates()
        this.load(true)
      }
    }
  },
  methods: {
    close() {
      this.visible = false
    },
    getCompanyDate() {
      const tz = this.companyTimeZone
      const now = new Date()
      const formatter = new Intl.DateTimeFormat('en-CA', {
        timeZone: tz,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      })
      return moment(formatter.format(now))
    },
    initializeDates() {
      if (this.initialStartDate && this.initialEndDate) {
        this.startDate = this.initialStartDate
        this.endDate = this.initialEndDate
        this.activePreset = 'custom'
      } else {
        const now = this.getCompanyDate()
        this.startDate = now.clone().startOf('year').format('YYYY-MM-DD')
        this.endDate = now.format('YYYY-MM-DD')
        this.activePreset = 'ytd'
      }
    },
    applyPreset(preset) {
      this.activePreset = preset.id
      const now = this.getCompanyDate()

      switch (preset.id) {
        case 'today':
          this.startDate = now.format('YYYY-MM-DD')
          this.endDate = now.format('YYYY-MM-DD')
          break
        case '7d':
          this.startDate = now.clone().subtract(6, 'days').format('YYYY-MM-DD')
          this.endDate = now.format('YYYY-MM-DD')
          break
        case 'month':
          this.startDate = now.clone().startOf('month').format('YYYY-MM-DD')
          this.endDate = now.format('YYYY-MM-DD')
          break
        case 'prev_month':
          this.startDate = now.clone().subtract(1, 'month').startOf('month').format('YYYY-MM-DD')
          this.endDate = now.clone().subtract(1, 'month').endOf('month').format('YYYY-MM-DD')
          break
        case 'qtd':
          this.startDate = now.clone().startOf('quarter').format('YYYY-MM-DD')
          this.endDate = now.format('YYYY-MM-DD')
          break
        case 'ytd':
          this.startDate = now.clone().startOf('year').format('YYYY-MM-DD')
          this.endDate = now.format('YYYY-MM-DD')
          break
      }
      this.load(true)
    },
    onManualDateChange() {
      if (this.startDate && this.endDate && this.startDate > this.endDate) {
        const tmp = this.startDate
        this.startDate = this.endDate
        this.endDate = tmp
      }
      this.activePreset = 'custom'
      this.load(true)
    },
    rankWidth(value, max) {
      return Math.max(8, Math.round((Number(value || 0) / Math.max(1, max)) * 100)) + '%'
    },
    monthlyFromDaily(days) {
      const buckets = {}
      const start = moment(this.startDate || moment().startOf('year')).startOf('month')
      const end = moment(this.endDate || undefined).startOf('month')
      const cursor = start.clone()
      while (cursor.isSameOrBefore(end, 'month')) {
        const key = cursor.format('YYYY-MM')
        buckets[key] = {
          date: key,
          label: cursor.format('MMM YYYY'),
          quoted: 0,
          confirmed: 0,
          invoiced: 0
        }
        cursor.add(1, 'month')
      }
      ;(days || []).forEach((row) => {
        const key = moment(row.date).format('YYYY-MM')
        if (!buckets[key]) return
        buckets[key].quoted += Number(row.quoted || 0)
        buckets[key].confirmed += Number(row.confirmed || 0)
        buckets[key].invoiced += Number(row.invoiced || 0)
      })
      return Object.keys(buckets).sort().map((key) => buckets[key])
    },
    buildChart() {
      const rows = this.chartRows || []
      this.lineData = {
        labels: rows.map((row) => row.label),
        datasets: [
          {
            label: 'Cotizado',
            data: rows.map((row) => Number(row.quoted || 0)),
            borderColor: '#94a3b8',
            backgroundColor: 'transparent',
            borderWidth: 2,
            fill: false,
            lineTension: 0.35,
            pointRadius: 2,
            pointBackgroundColor: '#94a3b8'
          },
          {
            label: 'Vendido',
            data: rows.map((row) => Number(row.confirmed || 0)),
            borderColor: '#0f766e',
            backgroundColor: 'rgba(15, 118, 110, 0.06)',
            borderWidth: 3,
            fill: true,
            lineTension: 0.35,
            pointRadius: 2,
            pointBackgroundColor: '#0f766e'
          },
          {
            label: 'Facturado',
            data: rows.map((row) => Number(row.invoiced || 0)),
            borderColor: '#2563eb',
            backgroundColor: 'transparent',
            borderWidth: 2,
            borderDash: [6, 4],
            fill: false,
            lineTension: 0.35,
            pointRadius: 2,
            pointBackgroundColor: '#2563eb'
          }
        ]
      }
    },
    deltaClass(value) {
      if (value == null) return ''
      if (value > 0) return 'is-up'
      if (value < 0) return 'is-down'
      return ''
    },
    deltaLabel(value) {
      if (value == null) return '—'
      const sign = value > 0 ? '+' : ''
      return sign + value + '%'
    },
    quoteStatus(status) {
      if (status === 'approved') return 'Aprobada'
      if (status === 'confirmed') return 'Confirmada'
      return 'Pendiente'
    },
    invoiceLabel(row) {
      const kind = Number(row.tipo) === 60 ? 'NC' : 'Factura'
      return row.folio ? kind + ' ' + row.folio : kind + ' #' + row.id
    },
    formatDate(value) {
      return value ? moment(value).format('DD/MM/YYYY') : '—'
    },
    formatDay(value) {
      return value ? moment(value).format('DD/MM/YYYY') : '—'
    },
    goToCustomerEdit() {
      this.$router.push({ name: 'CustomersCreateUpdate', params: { customerId: this.customerId } }).catch(() => {})
    },
    openQuote(id) {
      this.$router.push({ name: 'SalesNoteDetail', params: { sales_note: id } }).catch(() => {})
    },
    openInvoice(id) {
      this.$router.push({ name: 'CreateDTE', params: { id } }).catch(() => {})
    },
    openWorkOrder(id) {
      this.$router.push({ name: 'WorkOrderUpdate', params: { workOrder: id } }).catch(() => {})
    },
    async load(force) {
      if (!this.customerId) return
      const start = this.startDate || this.getCompanyDate().startOf('year').format('YYYY-MM-DD')
      const end = this.endDate || this.getCompanyDate().format('YYYY-MM-DD')
      const tz = encodeURIComponent(this.companyTimeZone)
      const query = `start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}&timezone=${tz}`

      if (this.requesting) return
      if (!force && this.lastQuery === query && this.asOf) return

      this.requesting = true
      this.loading = true
      this.error = ''
      try {
        const data = await fetchCustomerCommercialDashboard(this.customerId, query)
        if (!data || !data.as_of) throw new Error('Respuesta vacía del servidor')
        this.asOf = data.as_of
        this.updatedAt = data.updated_at || data.as_of
        this.serverTimezone = data.timezone || this.companyTimeZone
        this.lastQuery = query
        this.customer = data.customer || {}
        this.kpis = Object.assign({}, this.kpis, data.kpis || {})
        this.daily = data.daily || []
        this.openOrders = data.open_orders || []
        this.openQuotes = data.open_quotes || []
        this.latestInvoices = data.latest_invoices || data.recent_invoices || []
        this.products = data.products || []
        this.buildChart()
      } catch (err) {
        this.error = 'No se pudo cargar la información histórica del cliente.'
        notifications.showError('Error cargando histórico del cliente: ' + (err.message || err))
      } finally {
        this.requesting = false
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.modal-surface {
  background-color: #f8fafc;
  min-height: 100vh;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
}

.border-bottom {
  border-bottom: 1px solid rgba(15, 23, 42, 0.08);
}

.shadow-xs {
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04), 0 1px 2px rgba(15, 23, 42, 0.02);
}

.tracking-tight {
  letter-spacing: -0.02em;
}

.tracking-wide {
  letter-spacing: 0.04em;
}

/* Filter Card */
.filter-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
}

.compact-date-input {
  max-width: 155px;
}

/* Hero KPIs */
.hero-kpi-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.hero-kpi-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.06);
}

.hero-kpi-card.is-teal {
  border-color: #99f6e4;
  background: linear-gradient(180deg, #ffffff 0%, #f0fdfa 100%);
}

.kpi-label {
  font-size: 0.72rem;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.kpi-value {
  font-size: 1.55rem;
  font-weight: 800;
  letter-spacing: -0.03em;
  line-height: 1.2;
}

.kpi-footer {
  border-top: 1px solid #f1f5f9;
}

/* Pipeline Summary Cards */
.pipeline-summary-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
}

/* Panels */
.panel-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px 22px;
}

.panel-title {
  font-size: 0.95rem;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.01em;
}

.panel-sub {
  font-size: 12px;
  font-weight: 500;
  color: #64748b;
  margin-top: 2px;
}

.legend-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 16px;
  margin-bottom: 16px;
}

.legend {
  display: flex;
  gap: 16px;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
}

.swatch {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 6px;
}

.swatch.quoted { background: #94a3b8; }
.swatch.sold { background: #0f766e; }
.swatch.billed { background: #2563eb; }

/* Tables & Lists */
.history-table {
  background: transparent !important;
}

.history-table th {
  font-size: 11px !important;
  font-weight: 800 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.05em !important;
  color: #64748b !important;
  border-bottom: 1px solid #e2e8f0 !important;
}

.history-table td {
  font-size: 13px !important;
  border-bottom: 1px solid #f1f5f9 !important;
}

.click-row {
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.click-row:hover {
  background-color: #f8fafc !important;
}

.mini-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  margin-bottom: 6px;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.mini-row:hover {
  background-color: #f8fafc;
}

.rank-row {
  padding: 10px 12px;
  margin-bottom: 6px;
  border-radius: 8px;
}

.rank-name {
  font-size: 13px;
  color: #0f172a;
}

.rank-meta {
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
}

.rank-val {
  font-size: 13px;
  color: #0f172a;
  letter-spacing: -0.01em;
}

.rank-track {
  height: 5px;
  background: #e2e8f0;
  border-radius: 999px;
  overflow: hidden;
  margin-top: 4px;
}

.rank-fill {
  height: 100%;
  background: #0284c7;
  border-radius: 999px;
}

.rank-fill.is-teal { background: #0f766e; }

.delta {
  font-size: 0.72rem;
  font-weight: 800;
  color: #94a3b8;
}

.delta.is-up { color: #059669; }
.delta.is-down { color: #e11d48; }

.is-loss {
  color: #e11d48 !important;
}

.empty-mini {
  font-size: 13px;
  font-weight: 600;
  color: #94a3b8;
}
</style>
